import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  ArrowUpDown,
  ArrowUpRight,
  Calendar,
  Download,
  Eye,
  MousePointerClick,
  Percent,
  Plus,
  X,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { PageContainer } from '@/shared/components/layout/PageContainer';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard';
import { AppBadge } from '@/shared/components/ui/AppBadge';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { SessionOrganization } from '@/shared/types/entities';

import { useDashboardAnalytics, useCampaignAnalytics } from '../../api/analytics-queries';
import type {
  AnalyticsDashboardData,
  CampaignAnalytics,
  DateRangePreset,
  TopCampaign,
} from '../../types/analytics.types';
import styles from './AnalyticsDashboardPage.module.scss';

// ── Constants ──────────────────────────────────────────────────────────

const DATE_RANGE_OPTIONS: { label: string; value: DateRangePreset }[] = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
];

type SortField = 'spend' | 'roas';
type SortDirection = 'asc' | 'desc';

const PLATFORM_COLORS: Record<string, string> = {
  META: 'var(--color-info)',
  FACEBOOK: 'var(--color-info)',
  INSTAGRAM: 'var(--color-info)',
  GOOGLE: 'var(--color-warning)',
  TWITTER: 'var(--color-text)',
  X: 'var(--color-text)',
};

// ── Formatting helpers ─────────────────────────────────────────────────

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);
}

function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusToBadgeVariant(status: string): 'success' | 'warning' | 'error' | 'neutral' {
  switch (status.toUpperCase()) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'COMPLETED':
    case 'ENDED':
      return 'neutral';
    case 'REJECTED':
      return 'error';
    default:
      return 'neutral';
  }
}

// ── Derived data builders ──────────────────────────────────────────────

interface DashboardPoint {
  label: string;
  spend: number;
  revenue: number;
}

function buildPerformanceSeries(data: AnalyticsDashboardData): DashboardPoint[] {
  // Only real numbers: per-campaign spend and revenue (revenue = spend × ROAS,
  // which is exactly what the backend derived ROAS from). We don't yet receive
  // a real daily time series or revenue series, so we don't invent one — the
  // chart shows an empty state when there are no campaigns with spend.
  return data.topCampaigns.slice(0, 6).map((campaign) => ({
    label: campaign.name.length > 20 ? campaign.name.slice(0, 18) + '..' : campaign.name,
    spend: campaign.spendCents / 100,
    revenue: (campaign.spendCents / 100) * campaign.roas,
  }));
}

function buildCampaignSeries(campaign: CampaignAnalytics): DashboardPoint[] {
  return campaign.timeSeries.map((point) => ({
    label: point.date.slice(5, 10),
    spend: point.spendCents / 100,
    revenue: point.revenueCents / 100,
  }));
}

interface PlatformSummary {
  platform: string;
  spendCents: number;
  conversions: number;
  impressions: number;
  clicks: number;
  color: string;
}

function buildPlatformSummaries(data: AnalyticsDashboardData): PlatformSummary[] {
  // Only platforms the backend actually reported — no synthesized split.
  return data.platformBreakdown.map((pb) => ({
    ...pb,
    color: PLATFORM_COLORS[pb.platform.toUpperCase()] ?? 'var(--color-primary)',
  }));
}

function sortCampaigns(
  campaigns: TopCampaign[],
  field: SortField,
  direction: SortDirection,
): TopCampaign[] {
  return [...campaigns].sort((a, b) => {
    const aVal = field === 'spend' ? a.spendCents : a.roas;
    const bVal = field === 'spend' ? b.spendCents : b.roas;
    return direction === 'desc' ? bVal - aVal : aVal - bVal;
  });
}

function getOrganizationName(organizations: SessionOrganization[], orgId: string | null): string {
  return organizations.find((organization) => organization.id === orgId)?.name ?? 'your events';
}

// ── CSV Export ──────────────────────────────────────────────────────────

function exportDashboardCsv(data: AnalyticsDashboardData): void {
  const rows: string[][] = [['Campaign', 'Status', 'Spend ($)', 'ROAS']];

  for (const campaign of data.topCampaigns) {
    rows.push([
      campaign.name,
      campaign.status,
      (campaign.spendCents / 100).toFixed(2),
      campaign.roas.toFixed(2),
    ]);
  }

  rows.push([]);
  rows.push(['Metric', 'Value']);
  rows.push(['Total Spend', (data.totalSpendCents / 100).toFixed(2)]);
  rows.push(['Total Revenue', (data.totalRevenueCents / 100).toFixed(2)]);
  rows.push(['Average ROAS', data.averageRoas.toFixed(2)]);
  rows.push(['Total Impressions', String(data.totalImpressions)]);
  rows.push(['Total Clicks', String(data.totalClicks)]);
  rows.push(['Total Conversions', String(data.totalConversions)]);
  rows.push(['Active Campaigns', String(data.activeCampaigns)]);
  rows.push(['Total Campaigns', String(data.totalCampaigns)]);

  const csvContent = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `promote-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Component ──────────────────────────────────────────────────────────

export function AnalyticsDashboardPage() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRangePreset>('30d');
  const [sortField, setSortField] = useState<SortField>('spend');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  const { data, isLoading, isError } = useDashboardAnalytics(dateRange);
  const { data: selectedCampaign, isLoading: isSelectedCampaignLoading } = useCampaignAnalytics(
    selectedCampaignId ?? '',
    dateRange,
  );
  const orgId = useAuthStore((state) => state.orgId);
  const organizations = useAuthStore((state) => state.organizations);

  const organizationName = useMemo(
    () => getOrganizationName(organizations, orgId),
    [orgId, organizations],
  );

  const handleSortToggle = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
    },
    [sortField],
  );

  const handleRowClick = useCallback((campaignId: string) => {
    setSelectedCampaignId((prev) => (prev === campaignId ? null : campaignId));
  }, []);

  const selectedCampaignName = useMemo(() => {
    if (!data || !selectedCampaignId) return null;
    return data.topCampaigns.find((c) => c.id === selectedCampaignId)?.name ?? null;
  }, [data, selectedCampaignId]);

  const dashboardMetrics = useMemo(() => {
    if (!data) return [];

    if (selectedCampaignId && selectedCampaign) {
      const { totals } = selectedCampaign;

      return [
        {
          label: 'Impressions',
          value: formatCompactNumber(totals.impressions),
          helper: 'in selected range',
          icon: <Eye size={18} />,
        },
        {
          label: 'Clicks',
          value: formatCompactNumber(totals.clicks),
          helper: 'in selected range',
          icon: <MousePointerClick size={18} />,
        },
        {
          label: 'Click Through Rate',
          value: formatPercentage(totals.ctr),
          helper: 'in selected range',
          icon: <Percent size={18} />,
        },
        {
          label: 'ROAS',
          value: `${totals.roas.toFixed(2)}x`,
          helper: `${formatCurrency(totals.revenueCents)} revenue`,
          icon: <ArrowUpRight size={18} />,
        },
      ];
    }

    return [
      {
        label: 'Impressions',
        value: formatCompactNumber(data.totalImpressions),
        helper: 'in selected range',
        icon: <Eye size={18} />,
      },
      {
        label: 'Clicks',
        value: formatCompactNumber(data.totalClicks),
        helper: 'in selected range',
        icon: <MousePointerClick size={18} />,
      },
      {
        label: 'Click Through Rate',
        value: formatPercentage(data.averageCtr),
        helper: 'in selected range',
        icon: <Percent size={18} />,
      },
      {
        label: 'Active Promotions',
        value: String(data.activeCampaigns),
        helper: `${data.totalCampaigns} total`,
        icon: <ArrowUpRight size={18} />,
      },
    ];
  }, [data, selectedCampaignId, selectedCampaign]);

  const performanceSeries = useMemo(() => {
    if (selectedCampaignId && selectedCampaign) {
      return buildCampaignSeries(selectedCampaign);
    }

    return data ? buildPerformanceSeries(data) : [];
  }, [data, selectedCampaignId, selectedCampaign]);

  const platformSummaries = useMemo(() => (data ? buildPlatformSummaries(data) : []), [data]);

  const sortedCampaigns = useMemo(
    () => (data ? sortCampaigns(data.topCampaigns, sortField, sortDirection) : []),
    [data, sortField, sortDirection],
  );

  const handleExport = useCallback(() => {
    if (data) {
      exportDashboardCsv(data);
    }
  }, [data]);

  return (
    <PageContainer
      title="Event Promotions"
      subtitle={`Track ticket sales and ad performance across ${organizationName}.`}
    >
      <div className={styles.container}>
        {/* Header controls: date range + export */}
        <div className={styles.headerControls}>
          <div className={styles.dateRangeGroup}>
            <Calendar size={16} className={styles.dateIcon} />
            {DATE_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${styles.dateRangeButton} ${dateRange === option.value ? styles.dateRangeActive : ''}`}
                onClick={() => setDateRange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <AppButton
            size="sm"
            onClick={() => {
              void navigate({ to: '/promote/campaigns/new' });
            }}
          >
            <Plus size={14} />
            Create New Campaign
          </AppButton>
        </div>

        {/* Setup banner */}
        <AppCard className={styles.setupBanner}>
          <div className={styles.setupCopy}>
            <p className={styles.setupEyebrow}>Set up your event profile</p>
            <p className={styles.setupText}>
              Complete your profile to unlock AI-powered event promotion.
            </p>
          </div>
          <AppButton size="sm" className={styles.inlineButton}>
            Get Started
            <ArrowUpRight size={14} />
          </AppButton>
        </AppCard>

        {isLoading && (
          <div className={styles.loadingState}>
            <div className={styles.metricsGrid}>
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className={styles.skeletonCard} />
              ))}
            </div>
            <div className={styles.analyticsGrid}>
              <div className={styles.skeletonPanel} />
            </div>
            <div className={styles.skeletonList} />
          </div>
        )}

        {isError && (
          <AppCard className={styles.errorState}>
            Failed to load the dashboard. Please try again.
          </AppCard>
        )}

        {!isLoading && !isError && data && (
          <>
            {selectedCampaignId && selectedCampaignName && (
              <div className={styles.selectionBanner}>
                <div>
                  {isSelectedCampaignLoading ? (
                    <>
                      <LoadingSpinner size="sm" className={styles.inlineSpinner} />
                      Loading performance for <strong>{selectedCampaignName}</strong>…
                    </>
                  ) : (
                    <>
                      Showing performance for{' '}
                      <strong>{selectedCampaign?.campaignName ?? selectedCampaignName}</strong>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.clearSelectionButton}
                  onClick={() => setSelectedCampaignId(null)}
                >
                  <X size={14} />
                  Clear
                </button>
              </div>
            )}

            {/* Metric cards */}
            <section className={styles.metricsGrid}>
              {dashboardMetrics.map((metric) => (
                <AppCard key={metric.label} className={styles.metricCard}>
                  <div className={styles.metricIcon}>{metric.icon}</div>
                  <p className={styles.metricLabel}>{metric.label}</p>
                  <h2 className={styles.metricValue}>{metric.value}</h2>
                  <p className={styles.metricHelper}>
                    <span>{metric.helper}</span>
                  </p>
                </AppCard>
              ))}
            </section>

            {/* Performance chart */}
            <section className={styles.analyticsGrid}>
              <AppCard className={styles.chartPanel}>
                <div className={styles.panelHeader}>
                  <h3 className={styles.panelTitle}>
                    Performance Overview
                    {selectedCampaignId && selectedCampaign
                      ? ` — ${selectedCampaign.campaignName}`
                      : ''}
                  </h3>
                </div>
                <div className={styles.chartWrapper}>
                  {performanceSeries.length === 0 ? (
                    <div className={styles.chartEmpty}>
                      No spend yet in this range. Launch a campaign to see performance here.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={320}>
                      <LineChart
                        data={performanceSeries}
                        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid stroke="var(--color-border)" strokeDasharray="4 4" />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 12,
                            color: 'var(--color-text)',
                          }}
                          formatter={(value: number) => formatCurrency(value * 100)}
                        />
                        <Legend
                          verticalAlign="bottom"
                          formatter={(value) => (
                            <span className={styles.legendLabel}>
                              {value === 'revenue' ? 'revenue' : 'spend'}
                            </span>
                          )}
                        />
                        <Line
                          type="monotone"
                          dataKey="revenue"
                          stroke="var(--color-success)"
                          strokeWidth={3}
                          dot={{ r: 4, fill: 'var(--color-success)' }}
                          activeDot={{ r: 5 }}
                          name="revenue"
                        />
                        <Line
                          type="monotone"
                          dataKey="spend"
                          stroke="var(--color-primary)"
                          strokeWidth={3}
                          dot={{ r: 4, fill: 'var(--color-primary)' }}
                          activeDot={{ r: 5 }}
                          name="spend"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </AppCard>
            </section>

            {/* Platform breakdown */}
            {platformSummaries.length > 0 && (
              <section className={styles.platformSection}>
                <h3 className={styles.sectionTitle}>Platform Breakdown</h3>
                <div className={styles.platformGrid}>
                  {platformSummaries.map((ps) => {
                    const maxSpend = Math.max(...platformSummaries.map((p) => p.spendCents), 1);
                    const barWidth = (ps.spendCents / maxSpend) * 100;

                    return (
                      <AppCard key={ps.platform} className={styles.platformCard}>
                        <div className={styles.platformHeader}>
                          <span
                            className={styles.platformDot}
                            style={{ backgroundColor: ps.color }}
                          />
                          <span className={styles.platformName}>{toTitleCase(ps.platform)}</span>
                        </div>
                        <div className={styles.platformMetrics}>
                          <div className={styles.platformMetric}>
                            <span className={styles.platformMetricLabel}>Spend</span>
                            <span className={styles.platformMetricValue}>
                              {formatCurrency(ps.spendCents)}
                            </span>
                          </div>
                          <div className={styles.platformMetric}>
                            <span className={styles.platformMetricLabel}>Conversions</span>
                            <span className={styles.platformMetricValue}>
                              {new Intl.NumberFormat('en-US').format(ps.conversions)}
                            </span>
                          </div>
                          <div className={styles.platformMetric}>
                            <span className={styles.platformMetricLabel}>Clicks</span>
                            <span className={styles.platformMetricValue}>
                              {new Intl.NumberFormat('en-US').format(ps.clicks)}
                            </span>
                          </div>
                        </div>
                        <div className={styles.platformBar}>
                          <div
                            className={styles.platformBarFill}
                            style={{
                              width: `${barWidth}%`,
                              backgroundColor: ps.color,
                            }}
                          />
                        </div>
                      </AppCard>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Campaign performance table */}
            <section className={styles.promotionsSection}>
              <div className={styles.sectionHeader}>
                <div>
                  <h3 className={styles.sectionTitle}>Campaign Performance</h3>
                  <p className={styles.sectionSubtitle}>
                    {sortedCampaigns.length} campaigns ranked by{' '}
                    {sortField === 'spend' ? 'spend' : 'ROAS'}
                  </p>
                </div>
                <AppButton
                  variant="secondary"
                  size="sm"
                  className={styles.inlineButton}
                  onClick={handleExport}
                  disabled={!data || data.topCampaigns.length === 0}
                >
                  <Download size={14} />
                  Export CSV
                </AppButton>
              </div>

              {sortedCampaigns.length > 0 ? (
                <AppCard noPadding className={styles.tableCard}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.performanceTable}>
                      <thead>
                        <tr>
                          <th className={styles.thLeft}>Campaign</th>
                          <th className={styles.thLeft}>Status</th>
                          <th>
                            <button
                              type="button"
                              className={styles.sortButton}
                              onClick={() => handleSortToggle('spend')}
                            >
                              Spend
                              <ArrowUpDown size={12} />
                            </button>
                          </th>
                          <th>
                            <button
                              type="button"
                              className={styles.sortButton}
                              onClick={() => handleSortToggle('roas')}
                            >
                              ROAS
                              <ArrowUpDown size={12} />
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCampaigns.map((campaign) => (
                          <tr
                            key={campaign.id}
                            className={clsx(
                              styles.tableRow,
                              campaign.id === selectedCampaignId && styles.tableRowSelected,
                            )}
                            onClick={() => handleRowClick(campaign.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleRowClick(campaign.id);
                              }
                            }}
                            tabIndex={0}
                            aria-selected={campaign.id === selectedCampaignId}
                          >
                            <td className={styles.campaignNameCell}>{campaign.name}</td>
                            <td>
                              <AppBadge variant={statusToBadgeVariant(campaign.status)} size="sm">
                                {toTitleCase(campaign.status)}
                              </AppBadge>
                            </td>
                            <td className={styles.numericCell}>
                              {formatCurrency(campaign.spendCents)}
                            </td>
                            <td className={styles.numericCell}>
                              <span
                                className={
                                  campaign.roas >= 2
                                    ? styles.roasHigh
                                    : campaign.roas >= 1
                                      ? styles.roasMedium
                                      : styles.roasLow
                                }
                              >
                                {campaign.roas.toFixed(2)}x
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AppCard>
              ) : (
                <AppCard className={styles.emptyPromotions}>
                  <p>No campaign data yet. Create your first campaign to see performance here.</p>
                </AppCard>
              )}
            </section>
          </>
        )}
      </div>
    </PageContainer>
  );
}
